import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi } from '../../services/authApi';
import { setAuthToken, getAuthToken } from '../../services/apiClient';

/**
 * Session state.
 *
 * The token is the only thing kept in storage — the user record is re-fetched from
 * `/auth/me` on every boot, so an account that was disabled or deleted while the
 * tab was closed can't keep a stale session alive.
 */
const initialState = {
  user: null,
  status: 'idle', // idle | loading | authenticated | error
  error: null,
  fieldErrors: null,
  // true until the stored token has been checked, so the app doesn't flash the
  // login screen at someone who is already signed in
  restoring: Boolean(getAuthToken()),
};

export const login = createAsyncThunk('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const { token, user } = await authApi.login(email, password);
    setAuthToken(token);
    return user;
  } catch (err) {
    return rejectWithValue({ message: err.message, fields: err.fields });
  }
});

/** Validates the stored token on boot. A failure just means "sign in again". */
export const restoreSession = createAsyncThunk('auth/restore', async (_, { rejectWithValue }) => {
  try {
    const { user } = await authApi.me();
    return user;
  } catch (err) {
    setAuthToken(null);
    return rejectWithValue({ message: err.message });
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (s) => {
      setAuthToken(null);
      s.user = null;
      s.status = 'idle';
      s.error = null;
      s.fieldErrors = null;
      s.restoring = false;
    },
    clearAuthError: (s) => {
      s.error = null;
      s.fieldErrors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (s) => {
        s.status = 'loading';
        s.error = null;
        s.fieldErrors = null;
      })
      .addCase(login.fulfilled, (s, a) => {
        s.status = 'authenticated';
        s.user = a.payload;
      })
      .addCase(login.rejected, (s, a) => {
        s.status = 'error';
        s.error = a.payload?.message || 'Sign-in failed';
        s.fieldErrors = a.payload?.fields || null;
      })
      .addCase(restoreSession.pending, (s) => {
        s.restoring = true;
      })
      .addCase(restoreSession.fulfilled, (s, a) => {
        s.restoring = false;
        s.status = 'authenticated';
        s.user = a.payload;
      })
      .addCase(restoreSession.rejected, (s) => {
        s.restoring = false;
        s.status = 'idle';
        s.user = null;
      });
  },
});

export const { logout, clearAuthError } = authSlice.actions;

export const selectAuthUser = (st) => st.auth.user;
export const selectIsAuthenticated = (st) => Boolean(st.auth.user);
export const selectAuthStatus = (st) => st.auth.status;
export const selectAuthError = (st) => st.auth.error;
export const selectRestoring = (st) => st.auth.restoring;

export default authSlice.reducer;
